import React from 'react';
import styled from 'styled-components';

import { Alert, IconButton, LinearProgress, Stack } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { CloseFullscreenOutlined, OpenInFullOutlined, RefreshOutlined } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { DashboardCardType } from './Dashboard';
import { Dayjs } from 'dayjs';
import { TableToolbar } from '@common/Table/TableToolbar';

const Container = styled.div`
  flex: 1;
  height: 100%;
  border: 1px solid ${({ theme }) => theme.line.light};
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.background.light};
  min-width: 0;

  & .MuiDataGrid-root {
    border-radius: 0 0 6px 6px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;

  max-height: 50px;
  padding: 0 8px 0 16px;
  border-radius: 6px 6px 0 0;

  background-color: ${({ theme }) => theme.background.standard};
  border-bottom: 1px solid ${({ theme }) => theme.line.light};

  p {
    font-size: 16px;
    font-family: 'PPFormula';
    padding-top: 2px;
    color: ${({ theme }) => theme.primary};
  }
`;

interface Props {
  card: DashboardCardType;
  setFullScreenCard: React.Dispatch<React.SetStateAction<string | null>>;
  onClose?: () => void;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
}

export const DashboardTableCard: React.FC<Props> = ({ card, setFullScreenCard, onClose, startDate, endDate }) => {
  const { queryKey, queryFn, title, columns } = card;

  const {
    data = [],
    isLoading,
    isFetching,
    refetch,
    error,
  } = useQuery({
    queryKey: [queryKey, { startDate, endDate }],
    queryFn,
    retry: false,
    staleTime: 60000 * 15,
  });

  return (
    <Container>
      <Header>
        <p>{title}</p>
        <Stack direction='row' spacing={1} alignItems='center'>
          {!onClose && (
            <IconButton size='small' onClick={() => setFullScreenCard(queryKey)}>
              <OpenInFullOutlined fontSize='small' />
            </IconButton>
          )}
          {onClose && (
            <IconButton size='small' onClick={onClose}>
              <CloseFullscreenOutlined fontSize='small' />
            </IconButton>
          )}
          <IconButton size='small' onClick={() => refetch()} disabled={isFetching}>
            <RefreshOutlined />
          </IconButton>
        </Stack>
      </Header>
      {error ? (
        <Stack p={2}>
          <Alert severity='error'>{error?.message}</Alert>
        </Stack>
      ) : (
        <DataGrid
          sx={{ backgroundColor: 'white', border: 0 }}
          rows={data}
          columns={columns}
          autoPageSize
          getRowId={(row: any) => row.id}
          disableRowSelectionOnClick
          loading={isLoading || isFetching}
          density={'standard'}
          slots={{
            loadingOverlay: LinearProgress,
            toolbar: TableToolbar,
          }}
          slotProps={{
            toolbar: { toolbarSearchOnly: true },
          }}
        />
      )}
    </Container>
  );
};
